import React, { memo, useCallback, useLayoutEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material"

import { LocalizedEnum } from "../../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "inline-flex",
        padding: ".25rem",
        borderRadius: ".625rem",
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    wrap: {
        position: "relative",
        display: "flex",
    },
    highlight: {
        position: "absolute",
        top: 0,
        height: "100%",
        borderRadius: ".5rem",
        backgroundColor: theme.palette.common.white,
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s ease",
    },
    toggleButton: {
        position: "relative",
        textTransform: "none",
        border: "none",
        padding: ".375rem 1rem",
        fontWeight: 600,
        color: theme.palette.text.secondary,
        transition: "color 0.2s ease",

        "&.Mui-selected, &.Mui-selected:hover, &:hover": {
            color: theme.palette.text.primary,
            background: "transparent",
        },
    },
}))

function findActiveButton(container: HTMLElement, value: string) {
    const buttons = container.querySelectorAll<HTMLElement>(".MuiToggleButton-root")
    return Array.from(buttons).find((button) => button.getAttribute("value") === value)
}

type StandardSquareFilterProps<T extends string> = Readonly<{
    value: T
    onChange: (value: T) => void
    options: ReadonlyArray<T>
    translationBase: string
}>

function StandardSquareFilterInner<T extends string>({
    value,
    onChange,
    options,
    translationBase,
}: StandardSquareFilterProps<T>) {
    const { classes } = useStyles()
    const wrapRef = useRef<HTMLDivElement>(null)
    const [highlightStyle, setHighlightStyle] = useState({ left: 0, width: 0 })

    const updateHighlight = useCallback(() => {
        const container = wrapRef.current
        if (!container) return

        const activeButton = findActiveButton(container, value)
        if (!activeButton) return

        const { offsetLeft: left, offsetWidth: width } = activeButton
        setHighlightStyle((current) => (current.left === left && current.width === width ? current : { left, width }))
    }, [value])

    const updateHighlightRef = useRef(updateHighlight)

    useLayoutEffect(() => {
        updateHighlightRef.current = updateHighlight
        updateHighlight()
    }, [updateHighlight, options])

    useLayoutEffect(() => {
        const container = wrapRef.current
        if (!container) return

        const observer = new ResizeObserver(() => updateHighlightRef.current())
        observer.observe(container)

        return () => observer.disconnect()
    }, [])

    const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: T | null) => {
        if (newValue !== null && newValue !== value) {
            onChange(newValue)
        }
    }

    return (
        <Box className={classes.container}>
            <Box ref={wrapRef} className={classes.wrap}>
                <Box className={classes.highlight} style={highlightStyle} />
                <ToggleButtonGroup value={value} exclusive onChange={handleChange}>
                    {options.map((option) => (
                        <ToggleButton key={option} value={option} className={classes.toggleButton} disableRipple>
                            <LocalizedEnum base={translationBase} enumValue={option}>
                                <>{option}</>
                            </LocalizedEnum>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>
        </Box>
    )
}

export const StandardSquareFilter = memo(StandardSquareFilterInner) as typeof StandardSquareFilterInner
